import { useRef, type ComponentProps, type ReactNode, type RefObject } from 'react';
import { Drawer, DrawerContent } from '@voll-intelligence/ui-kit/ui/drawer';

/** Onde a folha monta — ver `DrawerContent.container`. */
export type BottomSheetContainer = ComponentProps<typeof DrawerContent>['container'];

/**
 * A folha que sobe do rodapé no palco ESTREITO — a pele do `Drawer` do DS com
 * as decisões que todas as folhas do produto compartilham: `bg-background`
 * (não o `bg-popover` de fábrica — os cartões dentro são `bg-card` e precisam
 * de um chão diferente pra ler como cartões); o foco volta para quem a abriu
 * (a abertura é controlada, sem `DrawerTrigger`, e sem isto o Base UI
 * devolveria o foco ao `body`); e a variante `fullscreen` (os filtros, o
 * detalhe do hotel) toma a tela inteira — sem os 6rem de respiro de fábrica,
 * sem o raio do topo e sem a alça de arrasto, e com o safe-area do notch somado
 * no topo — porque uma folha parcial sobre a lista lia como popover.
 *
 * Resgate do foco DERRUBADO: quando esta folha abre enquanto outra ainda sai de
 * cena (o preenchimento guiado, folha em folha), o Base UI chega a focar o
 * popup e, ao iniciar a transição de entrada, o foco cai no `body` — e a folha
 * que saiu tenta devolvê-lo a um popup já desmontado. Ao completar a abertura,
 * um foco no `body` volta ao popup; um foco que já pousou em algo vivo fica.
 *
 * Com `initialFocus` (a busca da folha de praça), o alvo é ELE, e o resgate vale
 * também pra um foco que ficou FORA da folha: a folha que sai de cena resolve a
 * devolução antes de esta abrir e a aplica depois (#4185), puxando o foco pro
 * gatilho de trás. Focar só ao completar a abertura é também o que pinta o
 * cursor no WebKit/iOS, que não o desenha num input focado com o popup ainda em
 * transição (`transform`/`will-change`).
 *
 * A de tela cheia não tem alça e não fecha por arraste (#6179): o gesto no conteúdo era o
 * da pessoa rolando a lista, e a folha ia embora com o que estava digitado. Fecha pelo
 * voltar do cabeçalho (`data-base-ui-swipe-ignore`: o Base UI não começa a dispensa dentro
 * dela). A parcial segue com a alça e o arraste de sempre.
 */
/** A pele da folha. Dentro de um contêiner (`container`), o teto de altura também respeita
 *  o CONTÊINER: o teto de fábrica (`100dvh - 6rem`) é da janela, e numa janela menor que
 *  ela (o diálogo dos pendentes) a folha passaria da borda de cima. O `100%` do `fixed`
 *  resolve contra o contêiner, que é quem a contém. */
function sheetClass(fullscreen: boolean, contained: boolean): string {
  if (fullscreen)
    return 'h-dvh bg-background pt-[env(safe-area-inset-top,0px)] data-[swipe-axis=y]:[--drawer-content-max-height:100dvh] data-[swipe-direction=down]:rounded-t-none data-[swipe-direction=down]:border-t-0';
  return contained
    ? 'bg-background data-[swipe-axis=y]:[--drawer-content-max-height:min(calc(100dvh-6rem),calc(100%-2rem))]'
    : 'bg-background';
}

export function BottomSheet({
  open,
  onOpenChange,
  returnFocusTo,
  label,
  fullscreen = false,
  initialFocus,
  container,
  children,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Quem recebe o foco quando a folha fecha — o controle que a abriu. A
   *  função é pra quem decide NA HORA do fechamento: devolve o alvo, ou `false`
   *  pra deixar o foco onde já está (o Confirmar das datas já pôs o foco no
   *  Buscar antes de a folha fechar, e a devolução ao gatilho desfaria isso). */
  readonly returnFocusTo: RefObject<HTMLElement | null> | (() => HTMLElement | false);
  /** O nome acessível do diálogo quando o conteúdo NÃO traz um `DrawerTitle`
   *  (os filtros têm o próprio cabeçalho). Ausente ⇒ o título nomeia. */
  readonly label?: string;
  readonly fullscreen?: boolean;
  /** O controle que recebe o foco ao abrir (o input da busca). Ausente ⇒ o
   *  padrão do Base UI. */
  readonly initialFocus?: RefObject<HTMLElement | null>;
  /** Onde a folha monta. Ausente ⇒ o `body`. Um contêiner com bloco de contenção próprio
   *  (o painel lateral do voucher no desktop) a faz subir DENTRO dele, na largura dele. */
  readonly container?: BottomSheetContainer;
  readonly children: ReactNode;
}): ReactNode {
  const popupRef = useRef<HTMLDivElement>(null);
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={(next) => {
        if (!next) return;
        const active = document.activeElement;
        const target = initialFocus?.current;
        if (target && (active === popupRef.current || !popupRef.current?.contains(active))) {
          target.focus({ preventScroll: true });
        } else if (target instanceof HTMLInputElement && active === target) {
          // Já focado desde a montagem (é o que sobe o teclado no iOS, dentro
          // do gesto): re-assentar a seleção repinta o cursor sem soltar o foco.
          target.setSelectionRange(target.selectionStart, target.selectionEnd);
        } else if (active === document.body) {
          popupRef.current?.focus({ preventScroll: true });
        }
      }}
      showSwipeHandle={!fullscreen}
    >
      <DrawerContent
        ref={popupRef}
        className={sheetClass(fullscreen, container !== undefined)}
        finalFocus={returnFocusTo}
        container={container}
        {...(fullscreen ? { 'data-base-ui-swipe-ignore': '' } : {})}
        {...(initialFocus ? { initialFocus } : {})}
        {...(label ? { 'aria-label': label } : {})}
      >
        {children}
      </DrawerContent>
    </Drawer>
  );
}
